import React, {ReactNode} from "react"
import {Link} from "react-router-dom"
import {AsyncResultRequestState} from "../../types/asyncResult"
import {ErrorDocument, OkDocument} from "../../resources/common"
import {getReceivedPayment, ReceivedPayment, ReceivedPaymentStatus} from "../../resources/receivedPayments"
import {Org} from "../../resources/org"
import {Customer} from "../../resources/customer"
import {Account} from "../../resources/account"
import {
	KeyValueCard,
	KeyValueCardContainer,
	KeyValueCardKey,
	KeyValueCardPending,
	KeyValueCardValue,
} from "../KeyValueCard/KeyValueCard"
import {kebabCase, startCase} from "lodash"
import {TagsViewerKeyValue} from "../Tags/TagsViewer"
import {useIsBankUser, useIsUnitUser} from "../../services/auth"
import {DrawerHeader} from "../Drawer/Drawer"
import classNames from "classnames"
import {LinkMetaBox, LinkMetaBoxContainer} from "../LinkMetaBox/LinkMetaBox"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import {AccountCard} from "../Accounts/AccountCard"
import {CustomerCard} from "../Customers/CustomerCard"
import {BeneficialOwnerCards} from "../BeneficialOwners/BeneficalOwnerCard"
import {extractReceivedPaymentFields, isRtpReceivedPayment} from "./ReceivedPayments.utils"
import {IncomingRealTimePaymentStatus} from "../../resources/incomingRealTimePayment"

interface ReceivedPaymentComponentProps {
	receivedPaymentId: string
	receivedPaymentAsyncResult?: AsyncResultRequestState<OkDocument<ReceivedPayment>, ErrorDocument>
	accessToken: string
	useDrawer: boolean
	refresh: () => void
}

export function ReceivedPaymentCounterpartyFields({receivedPayment}: {receivedPayment: ReceivedPayment}) {
	const {name, routingNumber, originatorEntityId} = extractReceivedPaymentFields(receivedPayment)

	const counterpartyDetails = {
		...(name ? {name} : {}),
		...(originatorEntityId ? {Identification: originatorEntityId} : {}),
		...(routingNumber ? {routingNumber} : {}),
	}

	return (
		<KeyValueCard title={"Counterparty Details"}>
			<KeyValueCardContainer>
				{Object.entries(counterpartyDetails).map(([key, value]) => {
					return (
						<React.Fragment key={key}>
							<KeyValueCardKey> {startCase(key)} </KeyValueCardKey>
							<KeyValueCardValue> {value} </KeyValueCardValue>
						</React.Fragment>
					)
				})}
			</KeyValueCardContainer>
		</KeyValueCard>
	)
}

function ReceivedPaymentInfoPending() {
	return (
		<div className="received-payment-content">
			<KeyValueCardPending rows={6} />
			<KeyValueCardPending rows={6} />
			<KeyValueCardPending rows={6} />
		</div>
	)
}

function getReceivedPaymentTransactionIds(receivedPayment: ReceivedPayment): string | undefined {
	return isRtpReceivedPayment(receivedPayment)
		? receivedPayment.relationships.receivePaymentTransaction?.data.id ?? undefined
		: undefined
}

const populateRtpReceivedPaymentFields = (receivedPayment: ReceivedPayment) => {
	const {
		description,
		name: counterpartyName,
		routingNumber: counterpartyRoutingNumber,
		accountNumber: counterpartyAccountNumber,
		rtpReturnReason: returnReason,
		rtpRejectedReason: rejectedReason,
		accountId,
	} = extractReceivedPaymentFields(receivedPayment)
	const transactionId = getReceivedPaymentTransactionIds(receivedPayment)
	const transactionLink =
		receivedPayment.attributes.status === ReceivedPaymentStatus.Completed && accountId && transactionId ? (
			<Link className="link" to={`/transaction/${accountId}/${transactionId}`}>
				{transactionId}
			</Link>
		) : undefined

	return {
		...(description ? {description} : {}),
		...(counterpartyName ? {counterpartyName} : {}),
		...(counterpartyRoutingNumber ? {counterpartyRoutingNumber} : {}),
		...(counterpartyAccountNumber ? {counterpartyAccountNumber} : {}),
		...(receivedPayment.attributes.status === IncomingRealTimePaymentStatus.Returned && returnReason
			? {returnReason}
			: {}),
		...(receivedPayment.attributes.status === IncomingRealTimePaymentStatus.Rejected && rejectedReason
			? {rejectedReason}
			: {}),
		...(transactionLink ? {transaction: transactionLink} : {}),
	} as Record<string, ReactNode>
}

export function ReceivedPaymentDetails({receivedPayment}: {receivedPayment: ReceivedPayment}) {
	const {amount, createdAt, estimatedCompletion, type, direction, reasonDisplay} =
		extractReceivedPaymentFields(receivedPayment)

	const receivedPaymentDetails = {
		...(amount ? {amount} : {}),
		...(createdAt ? {createdAt} : {}),
		...(type === "realtimeReceivedPayment" && populateRtpReceivedPaymentFields(receivedPayment)),
		...(estimatedCompletion ? {estimatedCompletion} : {}),
		...(type ? {type} : {}),
		...(direction ? {direction} : {}),
		...(type !== "realtimeReceivedPayment" && reasonDisplay ? {reason: reasonDisplay} : {}),
	}

	return (
		<KeyValueCard title={"Payment Details"}>
			<KeyValueCardContainer>
				{Object.entries(receivedPaymentDetails).map(([key, value]) => {
					return (
						<React.Fragment key={`payment-details-${key}`}>
							<KeyValueCardKey> {startCase(key)} </KeyValueCardKey>
							<KeyValueCardValue> {value} </KeyValueCardValue>
						</React.Fragment>
					)
				})}
			</KeyValueCardContainer>
		</KeyValueCard>
	)
}

export function ReceivedPaymentAdditionalDetails({receivedPayment}: {receivedPayment: ReceivedPayment}) {
	const tags = !isRtpReceivedPayment(receivedPayment)
		? receivedPayment.attributes.tags &&
		  Object.keys(receivedPayment.attributes.tags).length > 0 && (
				<TagsViewerKeyValue tags={receivedPayment.attributes.tags} />
		  )
		: undefined
	const {receivingEntityName, addenda, traceNumber, secCode, advanced} = extractReceivedPaymentFields(receivedPayment)

	const receivedPaymentAdditionalDetails = {
		...(receivingEntityName ? {receivingEntityName} : {}),
		...(addenda ? {addenda} : {}),
		...(tags ? {tags} : {}),
		...(traceNumber ? {traceNumber} : {}),
		...(secCode ? {secCode} : {}),
		...(advanced ? {advanced} : {}),
	}

	return (
		<KeyValueCard title={"Additional Payment Details"}>
			<KeyValueCardContainer>
				{Object.entries(receivedPaymentAdditionalDetails).map(([key, value]) => {
					return (
						<React.Fragment key={`payment-details-${key}`}>
							<KeyValueCardKey> {startCase(key)} </KeyValueCardKey>
							<KeyValueCardValue> {value} </KeyValueCardValue>
						</React.Fragment>
					)
				})}
			</KeyValueCardContainer>
		</KeyValueCard>
	)
}

function ReceivedPaymentInternal({
	receivedPayment,
	useDrawer,
	account,
	accessToken,
	customers,
	org,
}: {
	receivedPayment: ReceivedPayment
	accessToken: string
	refresh: () => void
	useDrawer: boolean
	customers?: Customer[]
	org?: Org
	account?: Account
}) {
	const {attributes} = receivedPayment
	const {accountId, transactionId, customer} = extractReceivedPaymentFields(receivedPayment)
	const shouldShowContentCardsSideBySide =
		(useIsUnitUser() || useIsBankUser()) && !useDrawer && attributes.status == ReceivedPaymentStatus.PendingReview
	return (
		<>
			{useDrawer && (
				<DrawerHeader
					title={`${receivedPayment.type.replace("ReceivedPayment", "").toUpperCase()} #${receivedPayment.id}`}
				>
					<div className={classNames("incoming-payments-status", kebabCase(attributes.status))}>
						{startCase(attributes.status)}
					</div>
				</DrawerHeader>
			)}

			<div className={classNames("received-payment-content", shouldShowContentCardsSideBySide && "pr-3")}>
				<ReceivedPaymentDetails receivedPayment={receivedPayment} />
				<ReceivedPaymentAdditionalDetails receivedPayment={receivedPayment} />
				{account && <AccountCard account={account} accessToken={accessToken} />}
				{customers?.map((customer) => {
					return (
						<React.Fragment key={customer.id}>
							<CustomerCard org={org} customer={customer} customerId={customer.id} />
							{customer.relationships.application && customer.type === "businessCustomer" && (
								<BeneficialOwnerCards
									applicationId={customer.relationships.application?.data.id}
									accessToken={accessToken}
								/>
							)}
						</React.Fragment>
					)
				})}
				<ReceivedPaymentCounterpartyFields receivedPayment={receivedPayment} />

				{useDrawer && (
					<KeyValueCard title={"Related"}>
						<KeyValueCardContainer>
							<LinkMetaBoxContainer>
								{accountId && (
									<LinkMetaBox
										text={"Account"}
										icon={"bank-account--business-products"}
										path={`/accounts/${accountId}`}
									/>
								)}
								{accountId && transactionId && (
									<LinkMetaBox
										text={"Transaction"}
										icon={"money-transfer--business-products"}
										path={`/transaction/${accountId}/${transactionId}`}
									/>
								)}

								{customer && (
									<LinkMetaBox
										key={`tag-${customer.id}`}
										text={`Customer #${customer.id}`}
										icon={"user-geometric-action-money-fund---users"}
										path={`/customers/${customer.id}`}
									/>
								)}
							</LinkMetaBoxContainer>
						</KeyValueCardContainer>
					</KeyValueCard>
				)}
			</div>
		</>
	)
}

export function ReceivedPaymentComponent({
	receivedPaymentId: receivedPaymentId,
	accessToken,
	useDrawer,
	refresh,
	receivedPaymentAsyncResult,
}: ReceivedPaymentComponentProps) {
	const receivedPaymentAsyncReq =
		receivedPaymentAsyncResult ||
		useAsyncResult(() =>
			getReceivedPayment(accessToken, receivedPaymentId, "customer,account,incomingAchItem,incomingWire")
		)

	return (
		<AsyncResultComponent asyncResult={receivedPaymentAsyncReq} pendingComponent={<ReceivedPaymentInfoPending />}>
			{({value: receivedPayment}) => {
				const include = receivedPayment.included
				const account = include && (include.find((i) => i.type === "depositAccount") as Account)
				const customers =
					include && (include.filter((i) => ["individualCustomer", "businessCustomer"].includes(i.type)) as Customer[])
				return (
					<ReceivedPaymentInternal
						useDrawer={useDrawer}
						receivedPayment={receivedPayment.data}
						customers={customers}
						accessToken={accessToken}
						refresh={refresh}
						account={account}
					/>
				)
			}}
		</AsyncResultComponent>
	)
}
